import { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, MenuItem, Select, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { MaskedField } from '@sysmobil/synoro-packages';
import phoneMaskArray from './phoneMaskArray.json';
import { requestCode } from 'js/library/utils/API/auth/requestCode';
import { useAuth } from 'js/contexts/AuthContext';
import { usePartnerUserForms } from 'js/contexts/PartnerUserFormsContext';

export default function InformCellphone() {
  const navigate = useNavigate();
  const { authState, setRequestCodeAuth, setRegistrationDraft } = useAuth();
  const { partnerId, indexKey } = usePartnerUserForms();
  const [country, setCountry] = useState(() => {
    const c = phoneMaskArray[0];
    return [c.ddd, c.mask, c.fullMask];
  });
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingExistingPhone, setCheckingExistingPhone] = useState(true);

  useEffect(() => {
    if (!authState.identify?.identifier) navigate('/login', { replace: true });
  }, [authState.identify?.identifier, navigate]);
  const phoneField = (authState.registrationDraft?.dynamicFields || []).find((field) =>
    ['cellPhone', 'phone', 'telephone'].includes(field?.fieldType)
  );
  const mask = phoneField?.inputMask || country[1];
  const existingPhone =
    authState.requestCode?.cellPhone ||
    (['cellPhone', 'phone', 'telephone'].includes(indexKey?.fieldType)
      ? authState.identify?.identifier
      : authState.registrationDraft?.cellphone ||
        authState.registrationDraft?.dynamicData?.contactList?.find(
          (item) => item.type === 'cellPhone'
        )?.value);
  const sendCode = useCallback(
    async (cellPhone) => {
      setLoading(true);
      try {
        const result = await requestCode({
          cellPhone,
          identifier: authState.identify.identifier,
          partnerId,
          newUser: authState.requestCode.newUser,
        });
        if (result?.error) throw new Error(result.error);
        setRegistrationDraft({
          ...authState.registrationDraft,
          cellphone: cellPhone,
          dynamicData: {
            ...(authState.registrationDraft?.dynamicData || {}),
            contactList: [
              ...(authState.registrationDraft?.dynamicData?.contactList || []).filter(
                (item) => item.type !== 'cellPhone'
              ),
              { type: 'cellPhone', value: cellPhone },
            ],
          },
        });
        setRequestCodeAuth({ cellPhone, id: result.id, newUser: authState.requestCode.newUser });
        navigate('/inserirCodigo');
      } catch (e) {
        setError(e?.message || 'Erro ao enviar SMS.');
      } finally {
        setLoading(false);
      }
    },
    [authState, partnerId, navigate, setRegistrationDraft, setRequestCodeAuth]
  );
  useEffect(() => {
    if (existingPhone && !authState.requestCode?.id) {
      setValue(existingPhone.replace(/^\+55/, ''));
      sendCode(existingPhone);
      return;
    }
    setCheckingExistingPhone(false);
  }, [existingPhone, sendCode, authState.requestCode?.id]);

  if (
    checkingExistingPhone ||
    authState.authData ||
    !authState.registrationDraft ||
    (authState.requestCode?.id && authState.requestCode?.cellPhone)
  ) {
    return null;
  }
  const submit = async (event) => {
    event.preventDefault();
    const cellPhone = `${country[0]}${value.replace(/\D/g, '')}`;
    if (cellPhone.replace(/\D/g, '').length < 12) return setError('Número de celular inválido.');
    await sendCode(cellPhone);
  };

  if (!existingPhone) {
    return (
      <Box
        component="form"
        onSubmit={submit}
        sx={{
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          p: 2,
        }}
      >
        <Typography variant="h5">Informe seu celular</Typography>
        <Box sx={{ display: 'flex', width: '100%', maxWidth: 450, gap: 1 }}>
          <Select
            value={JSON.stringify(country)}
            onChange={(e) => {
              setCountry(JSON.parse(e.target.value));
              setValue('');
            }}
          >
            <MenuItem value={JSON.stringify(country)}>🇧🇷 {country[0]}</MenuItem>
            {phoneMaskArray.slice(1).map((item) => (
              <MenuItem key={item.id} value={JSON.stringify([item.ddd, item.mask, item.fullMask])}>
                {item.emoji} {item.ddd}
              </MenuItem>
            ))}
          </Select>
          <MaskedField
            mask={mask}
            value={value}
            placeholder={mask}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
          />
        </Box>
        {error && <Typography color="error">{error}</Typography>}
        <Button type="submit" variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={22} /> : 'Continuar'}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{ minHeight: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
    >
      <CircularProgress size={22} />
    </Box>
  );
}
